/**
 * One registry for everything a user can *do*.
 *
 * Buttons, the command palette (Ctrl K), keyboard shortcuts and the shortcut overview
 * (`?`) all read from here, so an action exists once and every way of reaching it stays
 * in step. Pages register their own commands while they are mounted — the sheet editor
 * adds "Build", "Undo", "Switch to solutions" — and the palette shows whatever is live.
 *
 * Shortcuts are deliberately conservative: a bare key (`/`, `?`, `g s`) never fires
 * while a text field has the focus, so typing is never hijacked; only combinations with
 * Ctrl/⌘ or Alt work inside fields.
 */
import { modalOpen } from '../components/ui';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { getLocale, tx } from '../i18n';

export interface Command {
  id: string;
  title: string;
  /** heading in the palette and the shortcut overview */
  group: string;
  /** e.g. `mod+k`, `alt+arrowup`, `/`, `g s` (a sequence), `shift+?` */
  keys?: string;
  /** extra words the palette matches on */
  keywords?: string;
  /** hidden (and its keys inactive) while this returns false */
  when?: () => boolean;
  /**
   * Whether a Ctrl/Alt shortcut also fires while a text field has the focus (default
   * yes). Undo says no: in a field, Ctrl Z belongs to the field's own text.
   */
  inFields?: boolean;
  /**
   * Also fires while a dialog is open, for a key pressed inside it: a command of the
   * dialog's own content (the exercise editor in the sheet editor's drawer saves with
   * Ctrl S there too). Everything else stays behind the dialog.
   */
  inModal?: boolean;
  run: () => void;
}

interface Registry {
  register: (commands: Command[]) => () => void;
  commands: () => Command[];
  version: number;
  paletteOpen: boolean;
  setPaletteOpen: (open: boolean) => void;
  overlayOpen: boolean;
  setOverlayOpen: (open: boolean) => void;
}

const Ctx = createContext<Registry | null>(null);

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

/** How a key description is shown: `mod+k` → `Ctrl K` (or `⌘ K` on a Mac). */
export function formatKeys(keys: string): string {
  return keys
    .split(' ')
    .map((step) =>
      step
        .split('+')
        .map((k) => {
          const lower = k.toLowerCase();
          if (lower === 'mod') return isMac ? '⌘' : getLocale() === 'de' ? 'Strg' : 'Ctrl';
          if (lower === 'alt') return isMac ? '⌥' : 'Alt';
          if (lower === 'shift') return '⇧';
          if (lower === 'arrowup') return '↑';
          if (lower === 'arrowdown') return '↓';
          if (lower === 'enter') return '↵';
          if (lower === 'escape') return 'Esc';
          return k.length === 1 ? k.toUpperCase() : k[0]!.toUpperCase() + k.slice(1);
        })
        .join(' '),
    )
    .join(tx(' then '));
}

/**
 * Inputs that take no typing. A tick box or a radio button with the focus is not a text
 * field: counting every <input> as one switched `j`, `x` and Delete off as soon as a
 * row's tick box had been clicked.
 */
const NON_TEXT_INPUTS = new Set(['checkbox', 'radio', 'button', 'submit', 'reset', 'range', 'color', 'file', 'image']);

const isEditable = (el: EventTarget | null) =>
  el instanceof HTMLElement &&
  (el.isContentEditable ||
    (el.tagName === 'INPUT' && !NON_TEXT_INPUTS.has((el as HTMLInputElement).type)) ||
    el.tagName === 'TEXTAREA' ||
    el.tagName === 'SELECT' ||
    Boolean(el.closest('.cm-editor')));

/**
 * An open menu, list box or tree has the focus: its own keys move inside it. A bare `j`
 * or Delete there acted on the list behind the menu and pulled the focus out, leaving
 * the menu open over a selection that had moved.
 */
const inOwnKeyWidget = (el: EventTarget | null) =>
  el instanceof Element && Boolean(el.closest('[role=menu], [role=listbox], [role=tree]'));

/** Does one step (`mod+shift+z`) match this event? */
function stepMatches(step: string, e: KeyboardEvent): boolean {
  const parts = step.toLowerCase().split('+');
  const key = parts[parts.length - 1]!;
  const mod = parts.includes('mod');
  const alt = parts.includes('alt');
  const shift = parts.includes('shift');
  if (mod !== (isMac ? e.metaKey : e.ctrlKey)) return false;
  if (alt !== e.altKey) return false;
  // `?` is shift+/ on most layouts; compare the produced character for printable keys
  const produced = e.key.toLowerCase();
  // on layouts where `\` needs AltGr (German, among others), match the physical key
  if (key === '\\' && e.code === 'Backslash') return true;
  /*
   * With Alt a letter or digit is matched by its physical key too: on a Mac, Option 1
   * produces "¡" and Option X "≈", so `alt+1` never matched e.key there and the shortcut
   * the overview lists did nothing.
   */
  if (alt && /^[a-z0-9]$/.test(key) && e.code === (/[0-9]/.test(key) ? 'Digit' : 'Key') + key.toUpperCase()) {
    return shift === e.shiftKey;
  }
  if (key.length === 1) {
    if (produced !== key) return false;
    // shift is implied by the character itself for symbols like `?`
    if (/[a-z0-9]/.test(key) && shift !== e.shiftKey) return false;
    return true;
  }
  return shift === e.shiftKey && produced === key;
}

export function CommandsProvider({ children }: { children: ReactNode }) {
  const store = useRef(new Map<string, Command>());
  const [version, setVersion] = useState(0);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [overlayOpen, setOverlayOpen] = useState(false);

  const register = useCallback((commands: Command[]) => {
    for (const c of commands) store.current.set(c.id, c);
    setVersion((v) => v + 1);
    return () => {
      for (const c of commands) if (store.current.get(c.id) === c) store.current.delete(c.id);
      setVersion((v) => v + 1);
    };
  }, []);
  const commands = useCallback(() => [...store.current.values()].filter((c) => !c.when || c.when()), []);

  // the key handler: sequences like `g s` remember their first step for a second
  const pending = useRef<{ step: string; at: number } | null>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.isComposing) return;
      const inField = isEditable(e.target);
      const inMenu = inOwnKeyWidget(e.target);
      const live = [...store.current.values()].filter((c) => c.keys && (!c.when || c.when()));
      const now = Date.now();
      const prev = pending.current && now - pending.current.at < 1000 ? pending.current : null;
      // a dialog owns the keyboard: only the palette and the shortcut list reach past it
      const behindModal = modalOpen();
      const inDialog = e.target instanceof Element && Boolean(e.target.closest('[role=dialog]'));
      for (const c of live) {
        if (behindModal && c.id !== 'palette.open' && c.id !== 'help.shortcuts' && !(c.inModal && inDialog)) continue;
        const steps = c.keys!.split(' ');
        const needsModifier = /\b(mod|alt)\+/.test(c.keys!);
        if (inField && (!needsModifier || c.inFields === false)) continue;
        if (!needsModifier && inMenu) continue;
        const matched =
          steps.length === 1
            ? stepMatches(steps[0]!, e)
            : steps.length === 2 && prev?.step === steps[0] && stepMatches(steps[1]!, e);
        if (matched) {
          e.preventDefault();
          pending.current = null;
          c.run();
          return;
        }
      }
      // remember a possible first step of a sequence
      if (!inField && !e.ctrlKey && !e.metaKey && !e.altKey && e.key.length === 1) {
        const first = live.some((c) => c.keys!.split(' ').length === 2 && c.keys!.split(' ')[0] === e.key.toLowerCase());
        pending.current = first ? { step: e.key.toLowerCase(), at: now } : null;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const value = useMemo<Registry>(
    () => ({ register, commands, version, paletteOpen, setPaletteOpen, overlayOpen, setOverlayOpen }),
    [register, commands, version, paletteOpen, overlayOpen],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useRegistry(): Registry {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useRegistry outside CommandsProvider');
  return ctx;
}

/**
 * Register commands for as long as the calling component is mounted. `run` and `when`
 * are read through a ref, so passing fresh closures every render costs nothing and
 * always calls the latest one.
 */
export function useCommands(commands: Command[]): void {
  const { register } = useRegistry();
  const latest = useRef(commands);
  latest.current = commands;
  const signature = commands.map((c) => `${c.id}|${c.title}|${c.keys ?? ''}`).join('\n');
  useEffect(() => {
    const stable = latest.current.map((c) => ({
      ...c,
      run: () => latest.current.find((x) => x.id === c.id)?.run(),
      when: () => {
        const cur = latest.current.find((x) => x.id === c.id);
        return cur ? !cur.when || cur.when() : false;
      },
    }));
    return register(stable);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [register, signature]);
}
